/**
 * Capability manifest endpoint — step-up authorization wire types.
 *
 * Ports v1:
 *   - ``ando-frontend/app/types/capability.ts``
 *   - ``ando-frontend/app/api/capabilities.ts``
 *
 * BE source of truth:
 *   - ``ando-agent/src/api/v1/endpoints/capabilities.py`` (GET /capabilities/manifest)
 *   - ``ando-agent/src/services/capability/manifest.py``
 *
 * Realtime invalidation arrives via the typed WS event ``capabilities_changed``
 * (see ``packages/core/src/ws/types.ts``); the React provider re-fetches on it.
 *
 * Wire contract is FROZEN — discriminated union on ``state``:
 *   - "satisfied"   — user can use the capability; no banner.
 *   - "fixable"     — at least one upgrade_path exists; render banner.
 *   - "unreachable" — no upgrade path; render banner with disabled CTA.
 */

import { apiClient } from "../ApiClient.js";

/** Lifecycle state of a single capability for the authenticated user. */
export type CapabilityState = "satisfied" | "fixable" | "unreachable";

/** Action the user must perform on the upstream provider to satisfy scope. */
export type CapabilityUpgradeAction = "connect" | "reconnect";

/**
 * One way to fix a missing capability. Ranked by the backend (``rank=1`` =
 * recommended). ``consent_url`` is a backend-side OAuth start route; the FE
 * appends ``return_to_query_id`` so the agent can resume the interrupted
 * turn after the OAuth callback.
 */
export interface CapabilityUpgradePath {
  /** 1 = recommended; higher = alternative. */
  readonly rank: number;
  /** Provider identifier — e.g. "teams", "gmail", "salesforce". */
  readonly provider: string;
  /**
   * Capability bucket the provider exposes — e.g. ``"chat" | "email" |
   * "calendar" | "documents"``. White-label naming, never vendor-specific.
   */
  readonly connection_type: string;
  /** Stable per-connection identifier (email / org id / workspace handle). */
  readonly connection_key: string | null;
  readonly action: CapabilityUpgradeAction;
  /** Full backend OAuth start URL — FE only appends ``return_to_query_id``. */
  readonly consent_url: string;
  /** Italian CTA label rendered on the primary/secondary button. */
  readonly human_action_it: string;
  /** OAuth scopes the provider still needs to grant. */
  readonly missing_scopes: readonly string[];
}

/**
 * Per-capability descriptor. The manifest always exposes every capability
 * the BE knows about for the current user; only ``state`` decides whether
 * the banner renders.
 */
export interface CapabilityDiff {
  readonly capability: string;
  readonly state: CapabilityState;
  readonly label_it: string;
  readonly value_prop_it: string;
  readonly upgrade_paths: readonly CapabilityUpgradePath[];
}

/** Top-level shape of ``GET /api/v1/capabilities/manifest``. */
export interface CapabilityManifest {
  readonly user_id: string;
  /** Display-only configured personal scope; every query is authorized afresh. */
  readonly trama_lakehouse?: {
    readonly configured: boolean;
    readonly connection_id: string;
    /** Same-server mounted agentic API support, absent on older servers. */
    readonly api_agentic?: boolean;
    /** Same configured mixed human Console support; display-only, no grant. */
    readonly console_human_mixed?: boolean;
  };
  readonly generated_at: string;
  readonly capabilities: Readonly<Record<string, CapabilityDiff>>;
  /**
   * Ingest config the desktop consumes (Aug 26, 2026): the filesystem-scan
   * extension whitelist DERIVED server-side from the single canonical source
   * (``mime_types.SUPPORTED_EXTENSIONS``). Optional: older BE builds don't
   * ship it — consumers fall back to their static list.
   */
  readonly ingest?: {
    readonly filesystem_extensions?: readonly string[];
  };
}

/**
 * Tool-result payload emitted by an agentic tool (e.g. ``search_people``)
 * when the underlying provider lacks the required OAuth scope. The agent
 * message carries this object on ``message.metadata.tool_result`` so the
 * chat renderer can show an inline upgrade CTA under the bubble.
 */
export interface ScopeUpgradeRequiredToolResult {
  readonly status: "scope_upgrade_required";
  readonly capability: string;
  readonly user_message_it: string;
  readonly upgrade_paths: readonly CapabilityUpgradePath[];
}

/** Type guard for ``ScopeUpgradeRequiredToolResult``. */
export function isScopeUpgradeRequired(
  value: unknown,
): value is ScopeUpgradeRequiredToolResult {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    v.status === "scope_upgrade_required" &&
    typeof v.capability === "string" &&
    typeof v.user_message_it === "string" &&
    Array.isArray(v.upgrade_paths)
  );
}

/** GET ``/capabilities/manifest`` — fetch the user's current manifest. */
export async function fetchCapabilityManifest(): Promise<CapabilityManifest> {
  return await apiClient.get<CapabilityManifest>("/capabilities/manifest");
}
